import { GetUsersResponse, RegisterResponse } from '../types';
import type { CauseConfig, UpdateCausePayload } from '../types/cause';
import { API_CONFIG } from '../config/api';
import { getFunnelVariant } from '../config/funnelV2';
import { api as apiClient } from './apiClient';
import { buildEnterpriseQueryString, EnterpriseQueryOptions } from '../utils/enterpriseQuery';
import { isInternalTestEmail } from '../constants/internalTestEmails';

// Simple in-memory cache for API responses
const apiCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds cache

const getCachedData = <T>(key: string): T | null => {
    const cached = apiCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data as T;
    }
    return null;
};

const setCachedData = (key: string, data: any): void => {
    apiCache.set(key, { data, timestamp: Date.now() });
};

// Clear cache for specific keys (useful after mutations)
export const clearApiCache = (keyPattern?: string): void => {
    if (keyPattern) {
        const keys = Array.from(apiCache.keys());
        keys.forEach(key => {
            if (key.includes(keyPattern)) {
                apiCache.delete(key);
            }
        });
    } else {
        apiCache.clear();
    }
};

interface FetchUsersParams {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

interface UpdateProfileParams {
    firstName?: string;
    currentPassword?: string;
    password?: string;
    gender?: string;
    heightUnit?: string;
    weightUnit?: string;
    age?: number;
    height?: number;
    weight?: number;
    originWeight?: number;
    weightGoal?: number;
}

interface UpdateProfileResponse {
    success: boolean;
    data?: {
        user: any;
        passwordUpdated?: boolean;
    };
    message?: string;
}

interface AddCompanyUserResponse {
    success: boolean;
    data?: {
        user: {
            id: string;
            email: string;
            status: string;
            emailVerified: boolean;
            isPremium: boolean;
            createdAt: string;
        };
    };
    message?: string;
}

/**
 * Fetch app config (promotions, feature flags, etc.)
 * Public endpoint — no auth required
 */
export const fetchAppConfig = async (): Promise<{ success: boolean; data?: any }> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/config/app`);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch app config');
        }

        return data;
    } catch (error: any) {
        console.error('Fetch app config error:', error);
        throw error;
    }
};

/**
 * Fetch Stripe plans (public, no auth)
 */
export const fetchStripePlans = async (): Promise<{ success: boolean; data?: any[] }> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/plans`);
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch plans');
        }

        return data;
    } catch (error: any) {
        console.error('Fetch stripe plans error:', error);
        throw error;
    }
};

/**
 * Register a member via invitation (public, no auth)
 */
export const registerMember = async (params: {
    invitationKey: string;
    email: string;
    password: string;
    fullName?: string;
}): Promise<{ success: boolean; data?: any; error?: { message?: string }; message?: string }> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register-member`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            if (response.status === 503) {
                throw new Error('Our system is briefly busy. Please wait 30 seconds and try again — your invitation is still valid and your account will be created.');
            }
            if (response.status === 409) {
                throw new Error('EXISTING_EMAIL');
            }
            throw new Error(data.error?.message || data.message || 'Failed to register member');
        }

        return data;
    } catch (error: any) {
        if (error.message === 'EXISTING_EMAIL') {
            throw error;
        }
        console.error('Register member error:', error);
        throw error;
    }
};

export const queueMemberRegistration = async (params: {
    invitationKey: string;
    email: string;
    password: string;
    fullName?: string;
}): Promise<{ success: boolean; data?: { registrationId: string; status: string; clubName?: string } }> => {
    const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register-member-queued`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    });
    const data = await response.json();
    if (response.status === 409) {
        throw new Error('EXISTING_EMAIL');
    }
    if (response.status === 429 || response.status === 503) {
        throw new Error('Our system is briefly busy. Please wait 30 seconds and try again — your invitation is still valid.');
    }
    if (!response.ok || !data.success) {
        throw new Error(data.error?.message || data.message || 'Failed to register member');
    }
    return data;
};

export const getQueuedMemberRegistration = async (registrationId: string) => {
    const response = await fetch(
        `${API_CONFIG.BASE_URL}/auth/register-member/status/${registrationId}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Failed to check registration');
    }
    return data as {
        success: boolean;
        data?: {
            status: string;
            message?: string;
            position?: number;
            userId?: string;
            clubName?: string;
            tokens?: { accessToken: string; refreshToken: string };
        };
    };
};

/**
 * Existing user logs in and joins a club via invitation key (public, no auth)
 */
export const joinMemberInvite = async (params: {
    invitationKey: string;
    email: string;
    password: string;
}): Promise<{
    success: boolean;
    data?: {
        user?: any;
        tokens?: { accessToken: string; refreshToken: string };
        club?: { id: string; name: string };
        alreadyMember?: boolean;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/join-member-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.error?.message || data.message || 'Failed to join club');
        }

        return data;
    } catch (error: any) {
        console.error('Join member invite error:', error);
        throw error;
    }
};

/**
 * Validate a club invitation code (public, no auth)
 */
export const validateInvitation = async (invitationKey: string): Promise<{
    success: boolean;
    data?: { club: { id: string; name: string } };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/clubs/validate-invitation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invitationKey }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to validate invitation');
        }

        return data;
    } catch (error: any) {
        console.error('Validate invitation error:', error);
        throw error;
    }
};

export const fetchMembers = async (params: FetchUsersParams = {}): Promise<GetUsersResponse> => {
    const {
        page = 1,
        limit = 10,
        search = '',
        status = '',
        sortBy = 'createdAt',
        sortOrder = 'desc'
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    queryParams.append('sortBy', sortBy);
    queryParams.append('sortOrder', sortOrder);

    const response = await apiClient.get(
        `${API_CONFIG.ENDPOINTS.GET_USERS}?${queryParams}`
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch users');
    }

    return response as GetUsersResponse;
};

export const updateUserStatus = async (
    userId: string,
    status: string
): Promise<void> => {
    const response = await apiClient.patch(
        `${API_CONFIG.ENDPOINTS.UPDATE_USER_STATUS}/${userId}/status`,
        { status }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update user status');
    }
};

export interface OnboardingEmailCheckResult {
    exists: boolean;
    canContinue: boolean;
    requiresLogin: boolean;
    reason?: string;
    onboardingLastStep?: string;
    resumeCheckout?: {
        teamSize?: string;
        plan?: string;
        planId?: string;
        planType?: 'monthly' | 'yearly';
        amount?: number;
        currency?: string;
        companyName?: string;
    };
}

/**
 * Check if an email already exists in the system
 * @returns { exists: boolean }
 */
export const checkEmailExists = async (email: string): Promise<{ exists: boolean }> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/check-email`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to check email');
        }

        return { exists: data.data?.exists || false };
    } catch (error: any) {
        console.error('Check email error:', error);
        // Default to false to not block user flow on API error
        return { exists: false };
    }
};

/**
 * B2B onboarding email check — allows incomplete accounts to continue,
 * blocks paid/active accounts with a login redirect.
 */
export const checkOnboardingEmail = async (
    email: string
): Promise<OnboardingEmailCheckResult> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/check-email`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, flow: 'b2b_onboarding' })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to check email');
        }

        const result = data.data || {};
        return {
            exists: result.exists ?? false,
            canContinue: result.canContinue ?? !result.exists,
            requiresLogin: result.requiresLogin ?? false,
            reason: result.reason,
            onboardingLastStep: result.onboardingLastStep,
            resumeCheckout: result.resumeCheckout,
        };
    } catch (error: any) {
        console.error('Check onboarding email error:', error);
        return {
            exists: false,
            canContinue: true,
            requiresLogin: false,
        };
    }
};

/**
 * Capture lead email early in onboarding (before registration)
 * This adds the email to Loops for marketing follow-up
 * Sets userType: 'admin' automatically since only admins come through B2B funnel
 */
export interface CaptureLeadPayload {
    email: string;
    companyName?: string;
    source?: string;
    referralCode?: string;
    challengeName?: string;
    challengeStartDate?: string;
    challengeEndDate?: string;
    teamSize?: string;
    planName?: string;
    trialPrice?: number;
    mainGoal?: string;
    competitionType?: string;
    challengePurpose?: string;
    challengeType?: string;
    communicationTool?: string;
    onboardingLastStep?: string;
    causeType?: string;
    causeTitle?: string;
    teamCount?: number;
    prizePoolTotal?: number;
    dailyStepLiftPercent?: number;
}

export const captureLead = async (payload: CaptureLeadPayload): Promise<void> => {
    if (isInternalTestEmail(payload.email)) {
        return;
    }
    try {
        await fetch(
            `${API_CONFIG.BASE_URL}/marketing/lead`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );
    } catch (error: any) {
        // Don't block user flow if capture fails
        console.warn('Lead capture failed:', error.message);
    }
};

/**
 * Send marketing event to Loops for email automation
 * Events: checkout_started, payment_submitted, onboarding_completed, invite_sent, analytics_viewed, ongoing_mode_viewed
 */
export const sendMarketingEvent = async (
    email: string,
    eventName: string,
    properties: Record<string, string | number> = {}
): Promise<void> => {
    if (isInternalTestEmail(email)) {
        return;
    }
    try {
        await fetch(
            `${API_CONFIG.BASE_URL}/marketing/event`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, eventName, properties })
            }
        );
    } catch (error: any) {
        // Don't block user flow if event fails
        console.warn('Marketing event failed:', error.message);
    }
};

export const registerUser = async (
    email: string,
    password: string,
    fullName?: string,
    referralCode?: string,
    firstName?: string,
    lastName?: string,
    funnelDraft?: {
        companyName?: string | null;
        challengeStartDate?: string | null;
        challengeEndDate?: string | null;
        challengeType?: string | null;
        competitionType?: 'individual' | 'teams' | null;
        teamJoinMode?: 'admin' | 'self' | 'auto' | null;
        teams?: Array<{
            name: string;
            emoji: string;
            color: number;
            maxMembers: number | null;
        }> | null;
    }
): Promise<RegisterResponse> => {
    try {
        const requestBody: any = {
            email,
            password,
            role: 'admin', // Default to admin role for admin panel signups
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            funnelVariant: getFunnelVariant(),
        };

        if (fullName) {
            requestBody.fullName = fullName;
        }
        if (firstName) {
            requestBody.firstName = firstName;
        }
        if (lastName) {
            requestBody.lastName = lastName;
        }

        if (referralCode) {
            requestBody.referralCode = referralCode.toUpperCase();
        }

        if (funnelDraft) {
            if (funnelDraft.companyName) requestBody.companyName = funnelDraft.companyName;
            if (funnelDraft.challengeStartDate) requestBody.challengeStartDate = funnelDraft.challengeStartDate;
            if (funnelDraft.challengeEndDate) requestBody.challengeEndDate = funnelDraft.challengeEndDate;
            if (funnelDraft.challengeType) requestBody.challengeType = funnelDraft.challengeType;
            if (funnelDraft.competitionType) requestBody.competitionType = funnelDraft.competitionType;
            if (funnelDraft.teamJoinMode) requestBody.teamJoinMode = funnelDraft.teamJoinMode;
            if (funnelDraft.teams && funnelDraft.teams.length >= 2) requestBody.teams = funnelDraft.teams;
        }

        const response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REGISTER}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            }
        );

        const data: RegisterResponse = await response.json();

        if (!response.ok) {
            if (response.status === 503) {
                throw new Error('Our system is briefly busy. Please wait 30 seconds and try again — your invitation is still valid and your account will be created.');
            } else if (response.status === 429) {
                throw new Error('Too many registration attempts. Please try again later.');
            } else if (response.status === 400) {
                throw new Error(data.message || 'Invalid registration data');
            } else if (response.status === 409) {
                throw new Error('An account with this email already exists');
            } else {
                throw new Error(data.message || 'Registration failed. Please try again.');
            }
        }

        return data;
    } catch (error: any) {
        console.error('Registration error:', error);
        throw error;
    }
};

/**
 * Get current user profile from backend
 * Requires authentication
 */
/**
 * Get user profile - uses apiClient for automatic token refresh
 */
export const getProfile = async (skipCache = false): Promise<{ success: boolean; data?: { user: any }; message?: string }> => {
    const cacheKey = 'userProfile';

    // Check cache first (shorter TTL for profile - 10 seconds)
    if (!skipCache) {
        const cached = apiCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 10000) {
            return cached.data;
        }
    }

    const response = await apiClient.get<{ user: any }>('/users/profile');

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get profile');
    }

    // Cache the response
    setCachedData(cacheKey, response);

    return response;
};

/**
 * Update user profile
 * Requires authentication
 */
export const updateProfile = async (
    profileData: UpdateProfileParams
): Promise<UpdateProfileResponse> => {
    // Filter out undefined values
    const requestBody: any = {};
    Object.keys(profileData || {}).forEach((key) => {
        const value = (profileData as any)[key];
        if (value !== undefined && value !== null && value !== '') {
            requestBody[key] = value;
        }
    });

    const response = await apiClient.put('/users/profile', requestBody);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update profile');
    }

    return response as UpdateProfileResponse;
};

/**
 * Validate referral code
 * This is a PUBLIC endpoint - no authentication required
 */
export const validateReferralCode = async (
    referralCode: string
): Promise<{
    success: boolean;
    data?: {
        isValid: boolean;
        discountAmount?: number;
        discountType?: string;
        error?: string;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/referral/validate?ref=${referralCode.toUpperCase()}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                data: {
                    isValid: false,
                    error: data.message || 'Failed to validate referral code'
                }
            };
        }

        return data;
    } catch (error: any) {
        console.error('Validate referral code error:', error);
        return {
            success: false,
            data: {
                isValid: false,
                error: 'Failed to validate referral code'
            }
        };
    }
};

/**
 * Add company/test user with premium access (admin only)
 * Default password: 12345678
*/
interface CreateClubResponse {
    success: boolean;
    data?: {
        club: {
            _id: string;
            name: string;
            description?: string;
            adminId: string;
            members: any[];
            status: string;
            invitationKey: string;
            createdAt: string;
            updatedAt: string;
        };
    };
    message?: string;
}

/**
 * Create a new club
 * Requires authentication
 */
export const createClub = async (
    name: string,
    description?: string
): Promise<CreateClubResponse> => {
    const requestBody: any = { name };
    if (description) {
        requestBody.description = description;
    }

    const response = await apiClient.post(API_CONFIG.ENDPOINTS.CREATE_CLUB, requestBody);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to create club');
    }

    return response as CreateClubResponse;
};

/**
 * Complete onboarding club/challenge - Recovery endpoint
 * For users whose club wasn't created during registration
 */
export const completeOnboardingClub = async (): Promise<{
    success: boolean;
    data?: {
        alreadyComplete?: boolean;
        clubId: string;
        clubName: string;
        invitationKey: string;
        challengeId?: string;
        teamsCreated?: number;
    };
    message?: string;
}> => {
    const response = await apiClient.post('/clubs/complete-onboarding');

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to complete club setup');
    }

    return response;
};

/**
 * Complete onboarding subscription - Recovery endpoint
 * For users whose subscription wasn't created during registration
 */
export const completeOnboardingSubscription = async (): Promise<{
    success: boolean;
    data?: {
        alreadyComplete?: boolean;
        subscriptionId?: string;
        clientSecret?: string;
        planName?: string;
    };
    message?: string;
}> => {
    const response = await apiClient.post('/stripe/subscription/complete-onboarding');

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to complete subscription setup');
    }

    return response;
};

export const addCompanyUser = async (email: string): Promise<AddCompanyUserResponse> => {
    const response = await apiClient.post('/admin/users/company', { email });

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to add user');
    }

    return response as AddCompanyUserResponse;
};

/**
 * Get user's clubs
 * Requires authentication - uses apiClient for automatic token refresh
 */
export const getUserClubs = async (skipCache = false): Promise<{
    success: boolean;
    data?: { clubs: any[] };
    message?: string;
}> => {
    const cacheKey = 'userClubs';

    // Check cache first
    if (!skipCache) {
        const cached = getCachedData<{ success: boolean; data?: { clubs: any[] } }>(cacheKey);
        if (cached) {
            return cached;
        }
    }

    const response = await apiClient.get<{ clubs: any[] }>(API_CONFIG.ENDPOINTS.GET_USER_CLUBS);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch clubs');
    }

    // Cache the response
    setCachedData(cacheKey, response);

    return response;
};

export interface ClubAccessStatus {
    canManage: boolean;
    canParticipate: boolean;
    blockReason: 'payment_past_due' | 'subscription_canceled' | 'subscription_unpaid' | 'club_inactive' | null;
    title: string;
    message: string;
    actionLabel?: string;
    actionTab?: 'subscription';
}

export interface ManualInvoiceBilling {
    enabled: boolean;
    billingCycle: 'monthly' | 'quarterly' | 'annual' | 'custom';
    amount?: number;
    currency?: string;
    nextInvoiceDue?: string;
    lastInvoicePaidAt?: string;
    invoiceReference?: string;
    notes?: string;
    configuredAt?: string;
}

export interface ManagedClub {
    _id: string;
    name: string;
    invitationKey?: string;
    members?: any[];
    status?: string;
    enterpriseAccountId?: string;
    logoUrl?: string | null;
    coverImageUrl?: string | null;
    accessStatus: ClubAccessStatus;
    billingSummary: {
        subscriptionStatus: string | null;
        manualInvoiceEnabled: boolean;
        manualInvoiceBilling: ManualInvoiceBilling | null;
        currentPeriodEnd?: string | null;
    };
}

export const getManagedClubs = async (): Promise<{
    success: boolean;
    data?: { clubs: ManagedClub[] };
}> => {
    const response = await apiClient.get<{ clubs: ManagedClub[] }>('/clubs/managed');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch managed clubs');
    }
    return response as { success: boolean; data?: { clubs: ManagedClub[] } };
};

export interface SuperAdminDashboardClub {
    id: string;
    clubName: string;
    clubStatus: string;
    adminName: string;
    adminEmail: string;
    teamSize: number;
    subscriptionStatus: string;
    manualInvoiceBilling?: ManualInvoiceBilling | null;
}

export const getSuperAdminDashboardClubs = async (): Promise<{
    success: boolean;
    data?: { clubs: SuperAdminDashboardClub[]; metrics: Record<string, number> };
}> => {
    const response = await apiClient.get<{ clubs: SuperAdminDashboardClub[]; metrics: Record<string, number> }>(
        '/admin/dashboard/clubs',
    );
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch super admin clubs');
    }
    return response as { success: boolean; data?: { clubs: SuperAdminDashboardClub[]; metrics: Record<string, number> } };
};

export const setClubManualInvoice = async (
    clubId: string,
    payload: {
        enabled: boolean;
        billingCycle: ManualInvoiceBilling['billingCycle'];
        amount?: number | null;
        currency?: string | null;
        nextInvoiceDue?: string | null;
        lastInvoicePaidAt?: string | null;
        invoiceReference?: string | null;
        notes?: string | null;
    },
): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.patch(`/admin/clubs/${clubId}/manual-invoice`, payload);
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update manual invoice billing');
    }
    return response;
};

export const getClubDetails = async (clubId: string): Promise<{
    success: boolean;
    data?: { club: any };
    message?: string;
}> => {
    const response = await apiClient.get<{ club: any }>(`/clubs/${clubId}`);
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load club');
    }
    return response;
};

/**
 * Get challenges with filtering
 * Requires authentication - uses apiClient for automatic token refresh
 */
export const getChallenges = async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    clubId?: string;
    skipCache?: boolean;
}): Promise<{
    success: boolean;
    data?: {
        challenges: any[];
        pagination: any;
    };
    message?: string;
}> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.clubId) queryParams.append('clubId', params.clubId);

    const cacheKey = `challenges_${params?.clubId || 'all'}_${queryParams.toString()}`;

    // Check cache first
    if (!params?.skipCache) {
        const cached = getCachedData<{ success: boolean; data?: { challenges: any[]; pagination: any } }>(cacheKey);
        if (cached) {
            return cached;
        }
    }

    const endpoint = `${API_CONFIG.ENDPOINTS.GET_CHALLENGES}${
        queryParams.toString() ? `?${queryParams}` : ''
    }`;

    const response = await apiClient.get<{ challenges: any[]; pagination: any }>(endpoint);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch challenges');
    }

    // Cache the response
    setCachedData(cacheKey, response);

    return response;
};

/**
 * Create a new challenge
 * Requires authentication
 */
export const createChallenge = async (params: {
    clubId: string;
    name: string;
    description?: string;
    type: string;
    goal: number;
    startDate: string;
    endDate: string;
    adminTimezone?: string;
    // Team competition config
    competitionType?: 'individual' | 'teams';
    teams?: Array<{
        name: string;
        emoji: string;
        color: number;
        maxMembers?: number | null;
    }>;
    teamJoinMode?: 'admin' | 'self' | 'auto';
}): Promise<{
    success: boolean;
    data?: { challenge: any; teams?: any[] };
    message?: string;
}> => {
    const response = await apiClient.post<{ challenge: any; teams?: any[] }>(
        API_CONFIG.ENDPOINTS.CREATE_CHALLENGE,
        params
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to create challenge');
    }

    return response;
};

/**
 * Update challenge
 * Requires authentication (admin only)
 */
export const updateChallenge = async (
    challengeId: string,
    updates: {
        name?: string;
        description?: string;
        goal?: number;
        startDate?: string;
        endDate?: string;
        status?: string;
        /** Postpone a live challenge to a future start date */
        allowReschedule?: boolean;
        /** Clear early step totals when rescheduling (default true on server) */
        resetProgress?: boolean;
        webLeaderboardEnabled?: boolean;
        requireStepApproval?: boolean;
        teamCompetition?: {
            enabled: boolean;
            joinMode?: string;
            maxMembersPerTeam?: number | null;
        };
    }
): Promise<{
    success: boolean;
    data?: {
        challenge: any;
        billingInfo?: {
            isFirstChallenge: boolean;
            billingDateChanged: boolean;
            message: string;
        };
    };
    message?: string;
}> => {
    const response = await apiClient.patch<{
        challenge: any;
        billingInfo?: {
            isFirstChallenge: boolean;
            billingDateChanged: boolean;
            message: string;
        };
    }>(`${API_CONFIG.ENDPOINTS.UPDATE_CHALLENGE}/${challengeId}`, updates);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update challenge');
    }

    return response;
};

/**
 * Delete challenge
 * Requires authentication (admin only)
 */
export const deleteChallenge = async (
    challengeId: string
): Promise<{
    success: boolean;
    message?: string;
}> => {
    const response = await apiClient.delete(
        `${API_CONFIG.ENDPOINTS.DELETE_CHALLENGE}/${challengeId}`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to delete challenge');
    }

    return response;
};

/**
 * Get club members
 * Requires authentication (club admin only)
 */
export const getClubMembers = async (
    clubId: string,
    params: { page?: number; limit?: number } = {}
): Promise<{
    success: boolean;
    data?: {
        members: Array<{
            odId: string;
            odEmail: string;
            firstName: string | null;
            lastName?: string | null;
            avatar: string | null;
            joinedAt: string;
            role?: string;
            hasAppAccess?: boolean;
            lastLogin?: string;
            teamName?: string | null;
        }>;
        totalMembers?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
    };
    message?: string;
}> => {
    const { page = 1, limit = 100 } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const response = await apiClient.get(`/clubs/${clubId}/members?${queryParams}`);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to fetch club members');
    }

    return response;
};

/**
 * Remove member from club
 * Requires authentication (club admin only)
 */
export const removeClubMember = async (
    clubId: string,
    memberId: string
): Promise<{
    success: boolean;
    message?: string;
}> => {
    const response = await apiClient.delete(`/clubs/${clubId}/members/${memberId}`);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to remove member');
    }

    return response;
};

/**
 * Update club invitation code
 * Requires authentication (club admin only)
 */
export const updateClubCode = async (
    clubId: string,
    invitationKey: string
): Promise<{
    success: boolean;
    data?: { invitationKey: string };
    message?: string;
}> => {
    const response = await apiClient.put(
        `/clubs/${clubId}/invitation-key`,
        { invitationKey }
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update club code');
    }

    return response;
};

/**
 * Update club settings (e.g., hideWeightGoal)
 * Requires authentication (club admin only)
 */
export const updateClubSettings = async (
    clubId: string,
    settings: { hideWeightGoal?: boolean }
): Promise<{ success: boolean; data?: any; message?: string }> => {
    const response = await apiClient.patch<any>(`/clubs/${clubId}`, settings);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update club settings');
    }

    return response;
};

/**
 * Generate an admin invite link for a club
 * Requires authentication (club admin only)
 */
export const generateAdminInviteLink = async (
    email: string,
    clubId: string
): Promise<{
    success: boolean;
    data?: { inviteLink: string; expiresAt: string };
    message?: string;
}> => {
    const response = await apiClient.post<{ inviteLink: string; expiresAt: string }>(
        `/clubs/${clubId}/invite-admin`,
        { email }
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to generate admin invite link');
    }

    return response;
};

/**
 * Register a new user via admin invite token (public, no auth)
 */
export const registerAdminInvite = async (params: {
    token: string;
    firstName: string;
    lastName: string;
    password: string;
}): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any; clubId: string; clubName: string };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Failed to register');
        }
        return data;
    } catch (error: any) {
        console.error('Register admin invite error:', error);
        throw error;
    }
};

/**
 * Accept admin invite for an existing user (public, no auth — token carries identity)
 */
export const acceptAdminInvite = async (token: string, password?: string): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any; clubId: string; clubName: string };
    message?: string;
}> => {
    try {
        const body: Record<string, string> = { token };
        if (password) body.password = password;

        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/accept-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Failed to accept invite');
        }
        return data;
    } catch (error: any) {
        console.error('Accept admin invite error:', error);
        throw error;
    }
};

/**
 * Validate an admin invite token and get invite details (public, no auth)
 */
export const validateAdminInviteToken = async (token: string): Promise<{
    success: boolean;
    data?: {
        email: string;
        clubName: string;
        inviterName: string;
        isExistingUser: boolean;
        existingUserName?: string;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/validate-admin-invite?token=${encodeURIComponent(token)}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Invalid or expired invite');
        }
        return data;
    } catch (error: any) {
        console.error('Validate admin invite error:', error);
        throw error;
    }
};

/**
 * Invite user to club
 * Requires authentication (club admin only)
 */
export const inviteToClub = async (
    email: string,
    clubId: string
): Promise<{
    success: boolean;
    data?: { invitation: any };
    message?: string;
}> => {
    const response = await apiClient.post(
        API_CONFIG.ENDPOINTS.INVITE_TO_CLUB,
        { email, clubId }
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to send invitation');
    }

    return response;
};

// ============================================
// TEAM MANAGEMENT API FUNCTIONS
// ============================================

interface TeamMember {
    odId: string;
    firstName: string | null;
    avatar: string | null;
    gender: string | null;
    dailyAverage: number;
    totalSteps: number;
    completedDays: number;
}

export interface Team {
    _id: string;
    name: string;
    emoji: string;
    color: number;
    memberCount: number;
    maxMembers?: number | null;
    avgSteps?: number;
    totalSteps?: number;
    todaySteps?: number;
}

interface TeamDetailResponse {
    success: boolean;
    data?: {
        team: Team & { rank?: number };
        members: TeamMember[];
        currentUserTeamId: string | null;
        joinMode: string;
    };
    message?: string;
}

interface TeamsLeaderboardResponse {
    success: boolean;
    data?: {
        teams: Team[];
        userTeam: any;
        joinMode: string;
        maxPerTeam: number | null;
        memberTeamIds?: Record<string, string | null>;
    };
    message?: string;
}

/**
 * Get teams leaderboard for a challenge
 * Requires authentication
 */
export const getChallengeTeams = async (
    challengeId: string
): Promise<TeamsLeaderboardResponse> => {
    const response = await apiClient.get<TeamsLeaderboardResponse['data']>(
        `/challenges/${challengeId}/teams`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to fetch teams');
    }

    return response as TeamsLeaderboardResponse;
};

/**
 * Get team details with member list
 * Requires authentication
 */
export const getTeamDetail = async (
    challengeId: string,
    teamId: string
): Promise<TeamDetailResponse> => {
    const response = await apiClient.get<TeamDetailResponse['data']>(
        `/challenges/${challengeId}/teams/${teamId}`
    );

    if (!response.success) {
        const err: any = new Error(response.error?.message || response.message || 'Failed to fetch team details');
        err.status = response.error?.code;
        throw err;
    }

    return response as TeamDetailResponse;
};

/**
 * Update team members (add or remove)
 * Requires authentication (admin only)
 */
export const updateTeamMembers = async (
    challengeId: string,
    teamId: string,
    memberIds: string[],
    action: 'add' | 'remove'
): Promise<{
    success: boolean;
    data?: { team: Team };
    message?: string;
}> => {
    const response = await apiClient.patch<{ team: Team }>(
        `/challenges/${challengeId}/teams/${teamId}/members`,
        { memberIds, action }
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update team members');
    }

    return response;
};

/**
 * Join a team as a member (self-select mode)
 * Requires authentication
 */
export const joinTeam = async (
    challengeId: string,
    teamId: string
): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(
        `/challenges/${challengeId}/teams/${teamId}/join`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to join team');
    }

    return response;
};

/**
 * Leave a team as a member (self-select mode)
 * Requires authentication
 */
export const leaveTeam = async (
    challengeId: string,
    teamId: string
): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post(
        `/challenges/${challengeId}/teams/${teamId}/leave`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to leave team');
    }

    return response;
};

/**
 * Create a new team for a challenge
 * Requires authentication (admin only)
 */
export const createTeam = async (
    challengeId: string,
    team: { name: string; emoji: string; color: number; maxMembers?: number | null }
): Promise<{
    success: boolean;
    data?: { team: Team };
    message?: string;
}> => {
    const response = await apiClient.post<{ team: Team }>(
        `/challenges/${challengeId}/teams`,
        team
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to create team');
    }

    return response;
};

/**
 * Update a team's name, emoji, or color
 * Requires authentication (admin only)
 */
export const updateTeam = async (
    challengeId: string,
    teamId: string,
    team: { name?: string; emoji?: string; color?: number }
): Promise<{
    success: boolean;
    data?: { team: Team };
    message?: string;
}> => {
    const response = await apiClient.patch<{ team: Team }>(
        `/challenges/${challengeId}/teams/${teamId}`,
        team
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update team');
    }

    return response;
};

/**
 * Delete a team from a challenge
 * Requires authentication (admin only)
 */
export const deleteTeam = async (
    challengeId: string,
    teamId: string
): Promise<{
    success: boolean;
    data?: { message: string; remainingTeams: number };
    message?: string;
}> => {
    const response = await apiClient.delete<{ message: string; remainingTeams: number }>(
        `/challenges/${challengeId}/teams/${teamId}`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to delete team');
    }

    return response;
};

/**
 * Upload profile picture
 * Requires authentication
 */
export const uploadProfilePicture = async (
    imageBlob: Blob
): Promise<{
    success: boolean;
    data?: { avatar: string; avatarUrl: string };
    message?: string;
}> => {
    // Convert Blob to base64
    const base64Image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(imageBlob);
    });

    const response = await apiClient.post('/users/avatar', { image: base64Image });

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to upload profile picture');
    }

    return {
        success: true,
        data: {
            avatar: response.data?.avatarKey || response.data?.avatar,
            avatarUrl: response.data?.avatarUrl
        }
    };
};

// =====================================================
// Slack Integration API Functions
// Uses apiClient for automatic token refresh
// =====================================================

/**
 * Get Slack OAuth authorization URL for a club
 */
export const getSlackAuthUrl = async (
    clubId: string
): Promise<{ success: boolean; data?: { url: string }; message?: string }> => {
    const response = await apiClient.get<{ url: string }>(`/clubs/${clubId}/slack/auth-url`);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get Slack auth URL');
    }

    return response;
};

/**
 * Get available Slack channels for a club
 */
export const getSlackChannels = async (
    clubId: string
): Promise<{ success: boolean; data?: { channels: Array<{ id: string; name: string }> }; message?: string }> => {
    const response = await apiClient.get<{ channels: Array<{ id: string; name: string }> }>(
        `/clubs/${clubId}/slack/channels`
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get Slack channels');
    }

    return response;
};

/**
 * Set Slack channels for a club
 */
export const setSlackChannels = async (
    clubId: string,
    channels: {
        publicChannelId?: string | null;
        publicChannelName?: string | null;
        adminChannelId?: string | null;
        adminChannelName?: string | null;
    }
): Promise<{ success: boolean; data?: { slackIntegration: any }; message?: string }> => {
    const response = await apiClient.patch<{ slackIntegration: any }>(
        `/clubs/${clubId}/slack/channels`,
        channels
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to set Slack channels');
    }

    return response;
};

/**
 * Disconnect Slack integration from a club
 */
export const disconnectSlack = async (
    clubId: string
): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.delete(`/clubs/${clubId}/slack`);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to disconnect Slack');
    }

    return response;
};

// =====================================================
// Payment Issues Management API Functions
// Admin-only endpoints for fixing orphaned payments
// =====================================================

export interface PaymentIssue {
    userId: string;
    email: string;
    firstName: string | null;
    stripeAmount: number;
    stripeCurrency: string;
    paymentDate: string;
    hasCompletedGateway: boolean;
    hasDbPaymentRecord: boolean;
    paymentMethodStatus: 'attached' | 'orphaned' | 'error' | 'unknown';
    paymentMethodNeedsUpdate: boolean;
}

export interface FixPaymentResult {
    userId: string;
    email: string;
    actions: string[];
    stripePaymentFound?: boolean;
    stripeAmount?: number;
    stripeCurrency?: string;
}

/**
 * Get list of users with payment issues
 * (paid but payment not properly linked)
 */
export const getPaymentIssues = async (): Promise<{
    success: boolean;
    data?: PaymentIssue[];
    count?: number;
    message?: string;
}> => {
    const response = await apiClient.get<PaymentIssue[]>('/admin/users/payment-issues');

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch payment issues');
    }

    return response;
};

/**
 * Fix payment issues for a specific user
 * Options:
 * - markGatewayComplete: Mark hasCompletedGatewayPayment as true
 * - createPaymentRecord: Create payment record in DB if missing
 * - sendEmail: Send email to user to re-enter payment details
 */
export const fixUserPayment = async (
    userId: string,
    options: {
        markGatewayComplete?: boolean;
        createPaymentRecord?: boolean;
        sendEmail?: boolean;
    } = {}
): Promise<{
    success: boolean;
    data?: FixPaymentResult;
    message?: string;
}> => {
    const response = await apiClient.post<FixPaymentResult>(
        `/admin/users/${userId}/fix-payment`,
        options
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fix user payment');
    }

    return response;
};

// =====================================================
// Payment Method Update API Functions
// For users who need to update their payment details
// =====================================================

/**
 * Clear the paymentMethodNeedsUpdate flag after successful payment method update
 */
export const clearPaymentMethodUpdateFlag = async (): Promise<{
    success: boolean;
    message?: string;
}> => {
    const response = await apiClient.patch('/users/profile', {
        paymentMethodNeedsUpdate: false
    });

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update payment status');
    }

    return response;
};

/**
 * Fetch pending step entries awaiting admin approval
 */
export interface PendingStepEntry {
    stepId: string;
    userId: string;
    userName: string;
    userEmail?: string;
    date: string;
    steps: number;
    submittedAt?: string;
}

export const getPendingSteps = async (): Promise<{
    success: boolean;
    data?: PendingStepEntry[] | { entries: PendingStepEntry[] };
    message?: string;
}> => {
    const response = await apiClient.get<PendingStepEntry[] | { entries: PendingStepEntry[] }>('/steps/pending');

    if (!response.success) {
        const err: any = new Error(response.error?.message || 'Failed to fetch pending steps');
        err.status = response.error?.code ? parseInt(response.error.code) : 0;
        throw err;
    }

    return response;
};

export interface FlaggedStepEntry {
    stepId: string;
    userId: string;
    userName: string;
    userEmail?: string;
    date: string;
    steps: number;
    originalStepsReported: number;
    source?: string;
    deviceInfo?: string;
    flags: string[];
    flagLabels: string[];
    flagDescriptions?: string[];
    reviewPriority?: 'low' | 'medium' | 'high';
    reviewAction?: string;
    antiCheatDetails?: Record<string, string | number | boolean>;
    flaggedAt?: string;
}

/**
 * Fetch step records flagged by anti-cheat for admin review
 */
export const getFlaggedSteps = async (): Promise<{
    success: boolean;
    data?: FlaggedStepEntry[];
    message?: string;
}> => {
    const response = await apiClient.get<FlaggedStepEntry[]>('/steps/flagged');

    if (!response.success) {
        const err: any = new Error(response.error?.message || 'Failed to fetch flagged steps');
        err.status = response.error?.code ? parseInt(response.error.code) : 0;
        throw err;
    }

    return response;
};

/**
 * Approve or reject a pending step entry
 */
export const reviewPendingStep = async (
    stepId: string,
    action: 'approve' | 'reject',
    rejectionReason?: string
): Promise<{ success: boolean; message?: string }> => {
    const body: { stepId: string; action: string; rejectionReason?: string } = { stepId, action };
    if (action === 'reject' && rejectionReason) {
        body.rejectionReason = rejectionReason;
    }

    const response = await apiClient.post<void>('/steps/approve', body);

    if (!response.success) {
        throw new Error(response.error?.message || `Failed to ${action} step entry`);
    }

    return response;
};

/**
 * Bulk-approve all pending step entries for a challenge
 * Requires authentication (admin only)
 */
export const approveAllPendingSteps = async (
    challengeId: string
): Promise<{ success: boolean; data?: { approved: number }; message?: string }> => {
    const response = await apiClient.post(`/challenges/${challengeId}/steps/approve-all`);

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to bulk-approve steps');
    }

    return response;
};

/**
 * Get the member's active challenge (with web leaderboard info)
 */
export interface MemberChallengeInfo {
    _id: string;
    name: string;
    clubName?: string;
    startDate: string;
    endDate: string;
    goal: number;
    goalType: string;
    webLeaderboardEnabled: boolean;
    memberRank?: number;
    memberDailyAverage?: number;
    todaySubmitted?: boolean;
    todaySteps?: number;
    daysPassed?: number;
    totalDays?: number;
    teamCompetition?: {
        enabled: boolean;
        joinMode?: string;
    };
}

export const getMemberActiveChallenge = async (): Promise<{
    success: boolean;
    data?: { challenge: MemberChallengeInfo | null };
    message?: string;
}> => {
    const response = await apiClient.get<any>('/challenges/active');

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch active challenge');
    }

    // Normalize: backend returns data flat { _id, name, ... } or nested { challenge: { ... } }
    const challengeData = response.data?.challenge || response.data;

    return {
        success: response.success,
        data: challengeData ? { challenge: challengeData as MemberChallengeInfo } : undefined,
        message: response.message,
    };
};

/**
 * Get previous (completed) challenges for the member's club
 */
export const getPreviousChallenges = async (): Promise<{
    success: boolean;
    data?: Array<{ id: string; name: string; startDate: string; endDate: string }>;
}> => {
    const response = await apiClient.get<Array<{ id: string; name: string; startDate: string; endDate: string }>>('/challenges/previous');
    return { success: response.success, data: response.data || [] };
};

/**
 * Log steps via web browser
 */
export const logWebSteps = async (
    steps: number,
    date: string,
    timezone: string
): Promise<{ success: boolean; message?: string; data?: { stepId: string; totalStepsToday?: number } }> => {
    const response = await apiClient.post<{ stepId: string; totalStepsToday?: number }>('/steps/log-web', { steps, date, timezone });

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to submit steps');
    }

    return response;
};

/**
 * Get challenge leaderboard
 */
export interface LeaderboardEntry {
    rank: number;
    userId?: string;
    name?: string;
    firstName?: string;
    avatar?: string;
    gender?: string;
    dailyAverage: number;
    todaySteps: number;
    totalSteps?: number;
    isDay1?: boolean;
    completedDays?: number;
    source?: 'app' | 'manual' | 'web';
    hasPendingApproval?: boolean;
}

export interface LeaderboardPagination {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

export const getChallengeLeaderboard = async (
    challengeId: string,
    offset = 0,
    limit = 100
): Promise<{
    success: boolean;
    data?: { leaderboard: LeaderboardEntry[]; pagination?: LeaderboardPagination };
    message?: string;
}> => {
    const response = await apiClient.get<{ leaderboard: LeaderboardEntry[]; pagination?: LeaderboardPagination }>(
        `${API_CONFIG.ENDPOINTS.GET_CHALLENGES}/${challengeId}/leaderboard?limit=${limit}&offset=${offset}`
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch leaderboard');
    }

    return response;
};

/**
 * Challenge analytics — full event recap for admin dashboard
 * Returns metadata + 8 metrics + dailyChart + topIndividuals + topTeams (or null)
 */
export interface ChallengeAnalyticsResponse {
    success: boolean;
    data?: {
        challenge: {
            id: string;
            name: string;
            status: 'upcoming' | 'active' | 'finalizing' | 'completed' | string;
            startDate: string;
            endDate: string;
            daysTotal: number;
            daysElapsed: number;
            isFinal: boolean;
            adminTimezone?: string;
        };
        metrics: {
            totalSteps: number;
            averageStepsPerDay: number;
            totalParticipants: number;
            totalTeams: number;
            activeParticipants: number;
            participationRate: number;
            averageActiveDaysPerParticipant: number;
            averageStepsPerActiveMemberPerDay: number;
            mostActiveDay: { date: string; steps: number; activeMembers: number } | null;
            quietestDay: { date: string; steps: number; activeMembers: number } | null;
        };
        dailyChart: Array<{
            date: string;
            totalSteps: number;
            activeMembers: number;
        }>;
        members: Array<{
            rank: number;
            userId: string;
            firstName: string | null;
            lastName: string | null;
            avatar: string | null;
            totalSteps: number;
            activeDays: number;
            dailyAverage: number;
        }>;
        topIndividuals: Array<{
            rank: number;
            userId: string;
            firstName: string | null;
            lastName: string | null;
            avatar: string | null;
            totalSteps: number;
            activeDays: number;
            dailyAverage: number;
        }>;
        topTeams: Array<{
            rank: number;
            teamId: string;
            name: string;
            emoji?: string;
            color?: string;
            totalSteps: number;
            avgSteps: number;
            memberCount: number;
        }> | null;
        teamRosters: Array<{
            rank: number;
            teamId: string;
            name: string;
            emoji?: string;
            memberCount: number;
            avgSteps: number;
            totalSteps: number;
            members: Array<{
                userId: string;
                firstName: string;
                email: string;
                totalSteps: number;
                activeDays: number;
                avgStepsPerDay: number;
                dailyAverage: number;
            }>;
        }> | null;
        rankingsMatchLeaderboard: boolean;
        generatedAt: string;
    };
    message?: string;
}

export const getChallengeAnalytics = async (
    challengeId: string
): Promise<ChallengeAnalyticsResponse> => {
    const response = await apiClient.get<ChallengeAnalyticsResponse['data']>(
        `/challenges/${challengeId}/analytics`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to fetch analytics');
    }

    return response as ChallengeAnalyticsResponse;
};

/**
 * Public leaderboard entry (no auth required)
 */
export interface PublicLeaderboardEntry {
    rank: number;
    firstName: string;
    dailyAverage: number;
    todaySteps: number;
    isDay1?: boolean;
    completedDays?: number;
    hasPendingApproval?: boolean;
}

/**
 * Get public challenge leaderboard (no auth headers)
 */
export const getPublicChallengeLeaderboard = async (
    challengeId: string
): Promise<{
    success: boolean;
    data?: {
        leaderboard: PublicLeaderboardEntry[];
        challenge?: {
            name: string;
            startDate: string;
            endDate: string;
            goal: number;
            goalType: string;
        };
    };
    message?: string;
}> => {
    const response = await fetch(
        `${API_CONFIG.BASE_URL}/challenges/${challengeId}/leaderboard/public`
    );
    const responseData = await response.json();
    if (!response.ok) {
        const err: any = new Error(responseData.error?.message || responseData.message || 'Failed to fetch leaderboard');
        err.status = response.status;
        throw err;
    }
    return responseData;
};

/**
 * Get public team standings for a challenge (no auth required)
 */
export const getPublicChallengeTeams = async (
    challengeId: string
): Promise<{
    success: boolean;
    data?: {
        teams: Team[];
        teamCompetitionEnabled?: boolean;
        teamCompetition?: { enabled: boolean };
    };
}> => {
    const response = await fetch(
        `${API_CONFIG.BASE_URL}/challenges/${challengeId}/teams/public`
    );
    if (!response.ok) {
        // Non-team challenges return 404 — degrade gracefully
        return { success: false };
    }
    const responseData = await response.json();
    return responseData;
};

// ==========================================
// FEEDBACK / WALL OF LOVE
// ==========================================

export interface WallOfLoveEntry {
    id: string;
    userName: string;
    avatar: string | null;
    challengeName: string;
    rating: number;
    highlights: string[];
    comment: string | null;
    createdAt: string;
}

export interface WallOfLoveSummary {
    averageRating: number;
    totalFeedback: number;
    positiveFeedback: number;
}

export interface WallOfLoveResponse {
    wallEntries: WallOfLoveEntry[];
    summary: WallOfLoveSummary;
}

export const getWallOfLove = async (clubId: string): Promise<WallOfLoveResponse> => {
    const response = await apiClient.get(`/feedback/wall/${clubId}`);
    if (response.success && response.data) {
        return response.data;
    }
    throw new Error(response.error?.message || 'Failed to load feedback');
};

export interface PublicWallOfLoveEntry {
    userName: string;
    challengeName: string;
    rating: number;
    highlights: string[];
    comment: string | null;
    createdAt: string;
}

export interface PublicWallOfLoveResponse {
    clubName: string;
    logoUrl: string | null;
    wallEntries: PublicWallOfLoveEntry[];
    summary: WallOfLoveSummary;
}

/**
 * Public Wall of Love by slug (no auth)
 */
export const getPublicWallOfLove = async (slug: string): Promise<PublicWallOfLoveResponse> => {
    const response = await fetch(
        `${API_CONFIG.BASE_URL}/feedback/wall/public/${encodeURIComponent(slug)}`
    );
    const responseData = await response.json();
    if (!response.ok || !responseData.success || !responseData.data) {
        throw new Error(responseData.error || responseData.message || 'Wall of Love not found');
    }
    return responseData.data as PublicWallOfLoveResponse;
};

// ==========================================
// CAUSE MODE
// ==========================================

export type {
    CauseType,
    CauseFundingSource,
    CauseConversionMode,
    CauseCurrency,
    CauseMilestone,
    CauseExternalLink,
    CauseConfig,
    UpdateCausePayload,
} from '../types/cause';

/**
 * Get the cause configuration for a challenge
 * Requires authentication (admin only)
 */
export const getChallengeCause = async (
    challengeId: string
): Promise<{
    success: boolean;
    data?: CauseConfig | null;
    message?: string;
}> => {
    const response = await apiClient.get<CauseConfig | null>(
        `/challenges/${challengeId}/cause`
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to fetch cause configuration');
    }

    return response;
};

/**
 * Update the cause configuration for a challenge
 * Requires authentication (admin only)
 */
export const updateChallengeCause = async (
    challengeId: string,
    causeData: UpdateCausePayload
): Promise<{
    success: boolean;
    data?: CauseConfig;
    message?: string;
}> => {
    const response = await apiClient.patch<CauseConfig>(
        `/challenges/${challengeId}/cause`,
        causeData
    );

    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to update cause configuration');
    }

    return response;
};

// ==========================================
// REWARDS DASHBOARD
// ==========================================

export interface AdminRewardRedemption {
    orderId: string;
    userId: string;
    memberName: string;
    memberEmail: string | null;
    productName: string;
    amount: number;
    currency: string;
    status: 'fulfilled' | 'pending_admin_approval';
    pointsDeducted: number;
    budgetUsd: number | null;
    claimUrl?: string;
    createdAt: string | null;
}

export interface RewardsDashboardData {
    achievements?: {
        memberCount: number;
        membersWithAwards: number;
        totalAwards: number;
        badges: { id: string; name: string; members: number; awards: number }[];
        updatedAt: string;
    };
    clubId: string;
    rewardsEnabled: boolean;
    manualInvoiceBilling?: boolean;
    planTier: 'badges' | 'rewards' | 'moveband';
    currentPlan: {
        planId: string;
        planName: string;
        price: number;
        tierMin?: number;
        tierMax?: number;
    } | null;
    subscriptionStatus?: string | null;
    billingStartsAt?: string | null;
    canUpgradeToRewards: boolean;
    canTryRewardsFree?: boolean;
    upgradePlan: {
        planId: string;
        planName: string;
        price: number;
    } | null;
    upgradePricing?: {
        currentPlanName: string;
        currentPrice: number;
        newPlanName: string;
        newPrice: number;
        priceDifference: number | null;
        billingStartsAt: string | null;
        isTrialing: boolean;
    } | null;
    wallet: {
        rewards: {
            provider: 'xoxoday' | 'none';
            configured: boolean;
            balanceUsd?: number | null;
        };
        clubBudgetUsd: number;
        clubBudgetTopUpsUsd: number;
        includedMonthlyBudgetUsd: number;
        serviceFeeRate: number;
        memberPointsInCirculation: number;
        memberCount: number;
        forfeitedValueUsd?: number;
    };
    redemptions: {
        total: number;
        recent: AdminRewardRedemption[];
        pendingApproval: AdminRewardRedemption[];
    };
    winnerPrizes: {
        configured: boolean;
        latest: {
            paymentId: string;
            prizeAmount: number;
            handlingFee: number;
            totalPaid: number;
            breakdown: { first: number; second: number; third: number } | null;
            paidAt: string | null;
        } | null;
        history: Array<{
            paymentId: string;
            prizeAmount: number;
            handlingFee: number;
            totalPaid: number;
            breakdown: { first: number; second: number; third: number } | null;
            paidAt: string | null;
        }>;
    };
    activeChallengePrize: {
        challengeId: string;
        challengeName: string;
        prizeAmount?: number;
        prizeCurrency?: string;
        prizeDescription?: string;
        breakdown?: { first: number; second: number; third: number } | null;
        paymentCharged?: boolean;
        endDate?: string;
    } | null;
    canSetupWinnerPrizes?: boolean;
    eligibleChallengeForPrizes?: {
        challengeId: string;
        challengeName: string;
        status: string;
        startDate: string | null;
        endDate: string | null;
    } | null;
}

export const getRewardsDashboard = async (): Promise<{
    success: boolean;
    data?: RewardsDashboardData;
}> => {
    const response = await apiClient.get<RewardsDashboardData>('/clubs/rewards-dashboard');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch rewards dashboard');
    }
    return response;
};

export interface WinbackSummary {
    showCard: boolean;
    challengeId: string | null;
    challengeName: string | null;
    interestedCount: number;
    eligibleMemberCount: number;
    interestPercent: number;
    subscriptionCanceling: boolean;
    subscriptionCanceled: boolean;
    subscriptionPastDue: boolean;
    accessEndsAt: string | null;
    renewUrl: string;
    ongoingOfferAvailable: boolean;
    ongoingOfferUrl: string | null;
    reactivationMode: 'scheduled_cancel' | 'expired' | 'past_due' | 'none';
}

export const getWinbackSummary = async (): Promise<{
    success: boolean;
    data?: WinbackSummary;
}> => {
    const response = await apiClient.get<WinbackSummary>('/clubs/winback-summary');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch winback summary');
    }
    return response;
};

export const setupWinnerPrizes = async (breakdown: {
    first: number;
    second: number;
    third: number;
}): Promise<{
    success: boolean;
    data?: {
        challengeId: string;
        challengeName: string;
        prizeAmount: number;
        handlingFee: number;
        totalPaid: number;
        breakdown: { first: number; second: number; third: number };
        paymentIntentId: string;
        serviceFeeRate: number;
    };
}> => {
    const response = await apiClient.post<{
        challengeId: string;
        challengeName: string;
        prizeAmount: number;
        handlingFee: number;
        totalPaid: number;
        breakdown: { first: number; second: number; third: number };
        paymentIntentId: string;
        serviceFeeRate: number;
    }>('/clubs/setup-winner-prizes', { breakdown });
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to set up winner prizes');
    }
    return response;
};

export const activateRewards = async (mode?: 'try_free' | 'upgrade_now'): Promise<{
    success: boolean;
    data?: {
        rewardsEnabled: boolean;
        previousPlan?: string;
        newPlan?: string;
        billingMode?: 'deferred_until_trial_end' | 'immediate_proration' | 'already_on_rewards_plan';
        billingStartsAt?: string | null;
        message: string;
    };
}> => {
    const response = await apiClient.post<{
        rewardsEnabled: boolean;
        previousPlan?: string;
        newPlan?: string;
        billingMode?: 'deferred_until_trial_end' | 'immediate_proration' | 'already_on_rewards_plan';
        billingStartsAt?: string | null;
        message: string;
    }>('/clubs/activate-rewards', mode ? { mode } : {});
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to activate rewards');
    }
    clearApiCache('userClubs');
    return response;
};

export const topUpRewardsBudget = async (budgetUsd: number): Promise<{
    success: boolean;
    data?: {
        budgetUsd: number;
        serviceFeeUsd: number;
        totalChargedUsd: number;
        newClubBudgetUsd?: number;
        serviceFeeRate: number;
        paymentIntentId?: string;
        requiresAction?: boolean;
        clientSecret?: string | null;
        mandateText?: string;
        mandateEligible?: boolean;
    };
}> => {
    const response = await apiClient.post<{
        budgetUsd: number;
        serviceFeeUsd: number;
        totalChargedUsd: number;
        newClubBudgetUsd?: number;
        serviceFeeRate: number;
        paymentIntentId?: string;
        requiresAction?: boolean;
        clientSecret?: string | null;
        mandateText?: string;
        mandateEligible?: boolean;
    }>('/clubs/rewards-budget/top-up', { budgetUsd });

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to top up rewards wallet');
    }
    if (!response.data?.requiresAction) {
        clearApiCache('rewardsDashboard');
    }
    return response;
};

export const completeRewardsBudgetTopUp = async (paymentIntentId: string): Promise<{
    success: boolean;
    data?: {
        budgetUsd: number;
        serviceFeeUsd: number;
        totalChargedUsd: number;
        newClubBudgetUsd: number;
        serviceFeeRate: number;
        paymentIntentId: string;
    };
}> => {
    const response = await apiClient.post<{
        budgetUsd: number;
        serviceFeeUsd: number;
        totalChargedUsd: number;
        newClubBudgetUsd: number;
        serviceFeeRate: number;
        paymentIntentId: string;
    }>('/clubs/rewards-budget/top-up/complete', { paymentIntentId });

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to complete rewards top-up');
    }
    clearApiCache('rewardsDashboard');
    return response;
};

export const approveMemberRedemption = async (orderId: string): Promise<{
    success: boolean;
    data?: {
        order: {
            orderId?: string;
            productId: string;
            productName: string;
            amount: number;
            currency: string;
            status: string;
            claimUrl?: string;
            giftCardCode?: string;
            createdAt: string;
        };
        clubBudgetUsd: number;
    };
}> => {
    const response = await apiClient.post<{
        order: {
            orderId?: string;
            productId: string;
            productName: string;
            amount: number;
            currency: string;
            status: string;
            claimUrl?: string;
            giftCardCode?: string;
            createdAt: string;
        };
        clubBudgetUsd: number;
    }>(`/clubs/reward-redemptions/${encodeURIComponent(orderId)}/approve`, {});

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to approve redemption');
    }
    clearApiCache('rewardsDashboard');
    return response;
};

// ==========================================
// ENGAGED MEMBERS & RECOGNITION REWARDS
// ==========================================

export interface RewardFeedMember {
    userId: string;
    firstName: string;
    lastName?: string;
    avatar?: string | null;
    avatarUrl?: string | null;
    totalSteps: number;
    activeDays: number;
    todaySteps: number;
    reason: 'need_a_push' | 'supportive_teammate' | 'consistent_with_goal';
    reasonLabel: string;
    reasonDetail: string;
}

export interface RecognitionMemberSearchResult {
    userId: string;
    firstName: string;
    lastName?: string;
    avatar?: string | null;
    avatarUrl?: string | null;
    alreadyRewardedThisMonth: boolean;
}

/** @deprecated Use RewardFeedMember — kept for GiveRewardModal compatibility */
export type EngagedMember = RewardFeedMember;

export interface RewardFeedData {
    clubId: string;
    challengeId: string | null;
    challengeName: string | null;
    challengePhase?: 'active' | 'upcoming' | null;
    feed: RewardFeedMember[];
}

export const getRewardFeed = async (): Promise<{
    success: boolean;
    data?: RewardFeedData;
}> => {
    const response = await apiClient.get<RewardFeedData>('/clubs/engaged-members');
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch reward feed');
    }
    return response;
};

/** @deprecated Use getRewardFeed */
export const getEngagedMembers = getRewardFeed;

export const searchRecognitionMembers = async (
    query: string,
    limit = 10
): Promise<{
    success: boolean;
    data?: { members: RecognitionMemberSearchResult[] };
}> => {
    const params = new URLSearchParams();
    params.set('q', query.trim());
    params.set('limit', String(limit));

    const response = await apiClient.get<{ members: RecognitionMemberSearchResult[] }>(
        `/clubs/recognition-members/search?${params.toString()}`
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to search team members');
    }

    return response;
};

export interface EngagedMembersData extends RewardFeedData {
    /** @deprecated use feed */
    members?: RewardFeedMember[];
}

export const grantRecognitionReward = async (
    userId: string,
    payload: { points: number; message: string }
): Promise<{
    success: boolean;
    data?: {
        recognitionId: string;
        recipientUserId: string;
        recipientFirstName: string;
        points: number;
        message: string;
        newTotal: number;
    };
}> => {
    const response = await apiClient.post<{
        recognitionId: string;
        recipientUserId: string;
        recipientFirstName: string;
        points: number;
        message: string;
        newTotal: number;
    }>(`/clubs/members/${userId}/recognition-reward`, payload);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send recognition reward');
    }
    return response;
};

export const sendClubPushNotification = async (
    clubId: string,
    payload: { title: string; body: string }
): Promise<{
    success: boolean;
    data?: {
        announcementId: string;
        recipientCount: number;
        pushSentCount: number;
        pushSkippedCount: number;
    };
}> => {
    const response = await apiClient.post<{
        announcementId: string;
        recipientCount: number;
        pushSentCount: number;
        pushSkippedCount: number;
    }>(`/clubs/${clubId}/push-notification`, payload);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send push notification');
    }
    return response;
};

export interface ClubPushNotificationHistoryItem {
    id: string;
    title: string;
    body: string;
    adminDisplayName: string;
    recipientCount: number;
    pushSentCount: number;
    createdAt: string;
}

export const getClubPushNotificationHistory = async (
    clubId: string,
): Promise<{
    success: boolean;
    data?: {
        announcements: ClubPushNotificationHistoryItem[];
    };
}> => {
    const response = await apiClient.get<{
        announcements: ClubPushNotificationHistoryItem[];
    }>(`/clubs/${clubId}/push-notifications`);

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to load push notification history');
    }
    return response;
};

export interface EnterpriseAccountSummary {
    id: string;
    name: string;
    slug: string;
    status: string;
    basePlanTier: string;
    allowsTeamLeaderUpgrades: boolean;
}

export interface EnterpriseOverviewMetrics {
    enterpriseAccountId: string;
    enterpriseName: string;
    activeTeams: number;
    totalTeams: number;
    enrolledEmployees: number;
    participationRate: number;
    totalSteps: number;
    averageStepsPerActiveMemberPerDay: number;
    impactNarrative: string;
    generatedAt: string;
}

export interface EnterpriseTeamRow {
    clubId: string;
    clubName: string;
    adminUserId: string;
    adminName: string;
    adminEmail: string;
    memberCount: number;
    challengeId: string | null;
    challengeName: string | null;
    challengeStatus: string | null;
    participationRate: number;
    averageStepsPerDay: number;
    totalSteps: number;
    rewardsEnabled: boolean;
    hiddenFromComparison: boolean;
}

export interface EnterpriseTeamLeaderboardRow {
    rank: number;
    clubId: string;
    clubName: string;
    adminUserId: string;
    adminName: string;
    adminEmail: string;
    participantCount: number;
    weeklyActiveMembers: number;
    engagementRate: number;
    challengeName: string | null;
    challengeStatus: string | null;
}

export interface EnterpriseTeamLeaderboard {
    periodLabel: string;
    teams: EnterpriseTeamLeaderboardRow[];
}

export interface EnterpriseInviteInfo {
    enterpriseAccountId: string;
    enterpriseName: string;
    couponCode: string;
    signupUrl: string;
    instructions: string;
}

export interface EnterprisePulseItem {
    activityId: string;
    clubId: string;
    clubName: string;
    memberUserId: string;
    memberName: string;
    memberAvatarUrl?: string | null;
    type: string;
    workoutType?: string;
    title: string;
    note?: string;
    endedAt: string;
    metrics: {
        steps?: number;
        distanceMeters?: number;
        activeMinutes?: number;
    };
    cheerCount: number;
    commentCount: number;
}

export interface EnterprisePulseCategory {
    key: string;
    label: string;
    count: number;
    percent: number;
}

export interface EnterprisePulseAnalytics {
    totalShared: number;
    sharingChangePercent: number | null;
    comparisonLabel: string | null;
    categories: EnterprisePulseCategory[];
}

export interface EnterpriseMiniChallengeRow {
    miniChallengeId: string;
    clubId: string;
    clubName: string;
    hostUserId: string;
    hostName: string;
    title: string;
    subtitle?: string;
    emoji: string;
    kind: string;
    kindLabel: string;
    participantCount: number;
    status: string;
    createdAt: string;
    endDate: string;
}

export interface EnterpriseWellnessImpactTeamSnapshot {
    clubId: string;
    clubName: string;
    engagementRate: number;
    activeMembers: number;
    participantCount: number;
}

export interface EnterpriseWellnessImpact {
    periodLabel: string;
    totalTeams: number;
    totalMembers: number;
    activeMembersInPeriod: number;
    participationRate: number;
    activityChangePercent: number | null;
    mostActiveTeam: EnterpriseWellnessImpactTeamSnapshot | null;
    leastActiveTeam: EnterpriseWellnessImpactTeamSnapshot | null;
    headline: string;
    highlights: string[];
    narrative: string;
}

export interface EnterpriseTeamDetail {
    enterpriseAccountId: string;
    clubId: string;
    clubName: string;
    invitationKey: string;
    memberInviteUrl: string;
    adminUserId: string;
    adminName: string;
    adminEmail: string;
    memberCount: number;
    rewardsEnabled: boolean;
    hideWeightGoal: boolean;
    logoUrl: string | null;
    coverImageUrl: string | null;
    challenge: {
        id: string;
        name: string;
        status: string;
        participantCount: number;
        startDate: string;
        endDate: string;
    } | null;
    members: Array<{
        userId: string;
        firstName: string;
        email: string;
        role: string;
        hasAppAccess: boolean;
        joinedAt: string;
    }>;
}

export const getMyEnterpriseAccounts = async (): Promise<{
    success: boolean;
    data?: EnterpriseAccountSummary[];
}> => {
    const response = await apiClient.get<EnterpriseAccountSummary[]>('/enterprise/accounts/me');
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load enterprise accounts');
    }
    return response as { success: boolean; data?: EnterpriseAccountSummary[] };
};

export const getEnterpriseOverview = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month' }
): Promise<{ success: boolean; data?: EnterpriseOverviewMetrics }> => {
    const response = await apiClient.get<EnterpriseOverviewMetrics>(
        `/enterprise/accounts/${enterpriseAccountId}/overview${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load enterprise overview');
    }
    return response as { success: boolean; data?: EnterpriseOverviewMetrics };
};

export const getEnterpriseInviteInfo = async (
    enterpriseAccountId: string
): Promise<{ success: boolean; data?: EnterpriseInviteInfo }> => {
    const response = await apiClient.get<EnterpriseInviteInfo>(
        `/enterprise/accounts/${enterpriseAccountId}/invite`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load enterprise invite info');
    }
    return response as { success: boolean; data?: EnterpriseInviteInfo };
};

export const getEnterpriseTeams = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month', limit: 50 }
): Promise<{
    success: boolean;
    data?: {
        teams: EnterpriseTeamRow[];
        pagination: { currentPage: number; totalPages: number; totalCount: number };
    };
}> => {
    const response = await apiClient.get<{
        teams: EnterpriseTeamRow[];
        pagination: { currentPage: number; totalPages: number; totalCount: number };
    }>(`/enterprise/accounts/${enterpriseAccountId}/teams${buildEnterpriseQueryString(queryOptions)}`);
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load enterprise teams');
    }
    return response as {
        success: boolean;
        data?: {
            teams: EnterpriseTeamRow[];
            pagination: { currentPage: number; totalPages: number; totalCount: number };
        };
    };
};

export const getEnterpriseTeamLeaderboard = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'week' }
): Promise<{ success: boolean; data?: EnterpriseTeamLeaderboard }> => {
    const response = await apiClient.get<EnterpriseTeamLeaderboard>(
        `/enterprise/accounts/${enterpriseAccountId}/team-leaderboard${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load team leaderboard');
    }
    return response as { success: boolean; data?: EnterpriseTeamLeaderboard };
};

export type EnterpriseEngagementGranularity = 'week' | 'month';

export interface EnterpriseEngagementTrend {
    granularity: EnterpriseEngagementGranularity;
    periodLabel: string;
    points: Array<{ date: string; activeMembers: number; totalSteps: number }>;
}

export const getEnterpriseEngagementTrend = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month' }
): Promise<{
    success: boolean;
    data?: EnterpriseEngagementTrend;
}> => {
    const response = await apiClient.get<EnterpriseEngagementTrend>(
        `/enterprise/accounts/${enterpriseAccountId}/engagement-trend${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load engagement trend');
    }

    const trendPayload = response.data as EnterpriseEngagementTrend | { points: EnterpriseEngagementTrend['points'] } | undefined;
    if (trendPayload && 'points' in trendPayload && !('granularity' in trendPayload)) {
        const fallbackGranularity: EnterpriseEngagementGranularity = queryOptions.period === 'week' ? 'week' : 'month';
        return {
            success: true,
            data: {
                points: trendPayload.points,
                granularity: fallbackGranularity,
                periodLabel: fallbackGranularity === 'week' ? 'Last 12 weeks' : 'Last 6 months',
            },
        };
    }

    return response as {
        success: boolean;
        data?: EnterpriseEngagementTrend;
    };
};

export const getEnterprisePulse = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month', limit: 20 }
): Promise<{ success: boolean; data?: { items: EnterprisePulseItem[]; analytics: EnterprisePulseAnalytics } }> => {
    const response = await apiClient.get<{ items: EnterprisePulseItem[]; analytics: EnterprisePulseAnalytics }>(
        `/enterprise/accounts/${enterpriseAccountId}/pulse${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load team pulse');
    }
    return response as { success: boolean; data?: { items: EnterprisePulseItem[]; analytics: EnterprisePulseAnalytics } };
};

export const getEnterpriseMiniChallenges = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month', limit: 10 }
): Promise<{ success: boolean; data?: { items: EnterpriseMiniChallengeRow[] } }> => {
    const response = await apiClient.get<{ items: EnterpriseMiniChallengeRow[] }>(
        `/enterprise/accounts/${enterpriseAccountId}/mini-challenges${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load mini challenges');
    }
    return response as { success: boolean; data?: { items: EnterpriseMiniChallengeRow[] } };
};

export const getEnterpriseWellnessImpact = async (
    enterpriseAccountId: string,
    queryOptions: EnterpriseQueryOptions = { period: 'month' }
): Promise<{ success: boolean; data?: EnterpriseWellnessImpact }> => {
    const response = await apiClient.get<EnterpriseWellnessImpact>(
        `/enterprise/accounts/${enterpriseAccountId}/wellness-impact${buildEnterpriseQueryString(queryOptions)}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load wellness impact');
    }
    return response as { success: boolean; data?: EnterpriseWellnessImpact };
};

export const getEnterpriseTeamDetail = async (
    enterpriseAccountId: string,
    clubId: string
): Promise<{ success: boolean; data?: EnterpriseTeamDetail }> => {
    const response = await apiClient.get<EnterpriseTeamDetail>(
        `/enterprise/accounts/${enterpriseAccountId}/teams/${clubId}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load team details');
    }
    return response as { success: boolean; data?: EnterpriseTeamDetail };
};

export const inviteEnterpriseTeamMember = async (
    enterpriseAccountId: string,
    clubId: string,
    email: string,
    message?: string
): Promise<{ success: boolean; data?: { email: string; memberInviteUrl: string; sent: boolean } }> => {
    const response = await apiClient.post<{ email: string; memberInviteUrl: string; sent: boolean }>(
        `/enterprise/accounts/${enterpriseAccountId}/teams/${clubId}/invite-member`,
        { email, message }
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to send team invite');
    }
    return response as { success: boolean; data?: { email: string; memberInviteUrl: string; sent: boolean } };
};

export const exportEnterpriseReport = async (
    enterpriseAccountId: string,
    enterpriseName: string
): Promise<void> => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await fetch(
        `${API_CONFIG.BASE_URL}/enterprise/accounts/${enterpriseAccountId}/export`,
        {
            method: 'GET',
            headers: {
                Authorization: accessToken ? `Bearer ${accessToken}` : '',
            },
        }
    );

    if (!response.ok) {
        throw new Error('Failed to export enterprise report');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `enterprise-report-${enterpriseName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

export interface EnterpriseBranding {
    enterpriseAccountId: string;
    defaultLogoUrl: string | null;
    defaultCoverImageUrl: string | null;
    hasDefaultLogo: boolean;
    hasDefaultCoverImage: boolean;
}

export const getEnterpriseBranding = async (
    enterpriseAccountId: string
): Promise<{ success: boolean; data?: EnterpriseBranding }> => {
    const response = await apiClient.get<EnterpriseBranding>(
        `/enterprise/accounts/${enterpriseAccountId}/branding`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load enterprise branding');
    }
    return response as { success: boolean; data?: EnterpriseBranding };
};

export const uploadEnterpriseBrandingLogo = async (
    enterpriseAccountId: string,
    image: string
): Promise<{ success: boolean; data?: EnterpriseBranding }> => {
    const response = await apiClient.post<EnterpriseBranding>(
        `/enterprise/accounts/${enterpriseAccountId}/branding/logo`,
        { image }
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to upload enterprise logo');
    }
    return response as { success: boolean; data?: EnterpriseBranding };
};

export const uploadEnterpriseBrandingCover = async (
    enterpriseAccountId: string,
    image: string
): Promise<{ success: boolean; data?: EnterpriseBranding }> => {
    const response = await apiClient.post<EnterpriseBranding>(
        `/enterprise/accounts/${enterpriseAccountId}/branding/cover`,
        { image }
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to upload enterprise background');
    }
    return response as { success: boolean; data?: EnterpriseBranding };
};

export const deleteEnterpriseBrandingCover = async (
    enterpriseAccountId: string
): Promise<{ success: boolean; data?: EnterpriseBranding }> => {
    const response = await apiClient.delete<EnterpriseBranding>(
        `/enterprise/accounts/${enterpriseAccountId}/branding/cover`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to remove enterprise background');
    }
    return response as { success: boolean; data?: EnterpriseBranding };
};

export const uploadClubLogo = async (
    clubId: string,
    image: string
): Promise<{ success: boolean; data?: { clubId: string; logoUrl: string } }> => {
    const response = await apiClient.post<{ clubId: string; logo: string }>(
        `/clubs/${clubId}/logo`,
        { image }
    );
    if (!response.success || !response.data) {
        throw new Error(response.error?.message || response.message || 'Failed to upload club logo');
    }
    return {
        success: true,
        data: {
            clubId: response.data.clubId,
            logoUrl: response.data.logo,
        },
    };
};

export const uploadClubCover = async (
    clubId: string,
    image: string
): Promise<{ success: boolean; data?: { clubId: string; coverImageUrl: string } }> => {
    const response = await apiClient.post<{ clubId: string; coverImage: string }>(
        `/clubs/${clubId}/cover`,
        { image }
    );
    if (!response.success || !response.data) {
        throw new Error(response.error?.message || response.message || 'Failed to upload club background');
    }
    return {
        success: true,
        data: {
            clubId: response.data.clubId,
            coverImageUrl: response.data.coverImage,
        },
    };
};

export const deleteClubCover = async (
    clubId: string
): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/clubs/${clubId}/cover`);
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to remove club background');
    }
    return { success: true };
};

export interface EnterpriseOrgAdminSummary {
    userId: string;
    email: string;
    firstName: string;
    lastName?: string;
    role?: string;
    lastLogin?: string;
    invitedAt?: string;
}

export interface EnterpriseOrgAdminAccessSummary {
    enterpriseName: string;
    admins: EnterpriseOrgAdminSummary[];
    inviteLink: string;
    inviteCode: string;
    canManageAdmins: boolean;
}

export const getEnterpriseAdminAccess = async (
    enterpriseAccountId: string
): Promise<{ success: boolean; data?: EnterpriseOrgAdminAccessSummary }> => {
    const response = await apiClient.get<EnterpriseOrgAdminAccessSummary>(
        `/enterprise/accounts/${enterpriseAccountId}/admin-invites`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load org admin settings');
    }
    return response as { success: boolean; data?: EnterpriseOrgAdminAccessSummary };
};

export const removeEnterpriseOrgAdmin = async (
    enterpriseAccountId: string,
    adminUserId: string
): Promise<{ success: boolean; data?: EnterpriseOrgAdminAccessSummary }> => {
    const response = await apiClient.delete<EnterpriseOrgAdminAccessSummary>(
        `/enterprise/accounts/${enterpriseAccountId}/admins/${adminUserId}`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to remove org admin');
    }
    return response as { success: boolean; data?: EnterpriseOrgAdminAccessSummary };
};

export const regenerateEnterpriseAdminInviteLink = async (
    enterpriseAccountId: string
): Promise<{ success: boolean; data?: EnterpriseOrgAdminAccessSummary }> => {
    const response = await apiClient.post<EnterpriseOrgAdminAccessSummary>(
        `/enterprise/accounts/${enterpriseAccountId}/admin-invites`
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to regenerate org admin invite link');
    }
    return response as { success: boolean; data?: EnterpriseOrgAdminAccessSummary };
};

export const validateEnterpriseAdminInviteCode = async (code: string): Promise<{
    success: boolean;
    data?: {
        enterpriseAccountId: string;
        enterpriseName: string;
        inviteCode: string;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/validate-enterprise-admin-invite?code=${encodeURIComponent(code)}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Invalid or expired invite');
        }
        return data;
    } catch (error: any) {
        console.error('Validate enterprise admin invite error:', error);
        throw error;
    }
};

/** @deprecated Use validateEnterpriseAdminInviteCode */
export const validateEnterpriseAdminInviteToken = async (token: string): Promise<{
    success: boolean;
    data?: {
        email: string;
        enterpriseAccountId: string;
        enterpriseName: string;
        inviterName: string;
        isExistingUser: boolean;
        existingUserName?: string | null;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/validate-enterprise-admin-invite?token=${encodeURIComponent(token)}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Invalid or expired invite');
        }
        return data;
    } catch (error: any) {
        console.error('Validate enterprise admin invite error:', error);
        throw error;
    }
};

export const registerEnterpriseAdminInvite = async (params: {
    code: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
}): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register-enterprise-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Failed to register');
        }
        return data;
    } catch (error: any) {
        console.error('Register enterprise admin invite error:', error);
        throw error;
    }
};

export const acceptEnterpriseAdminInvite = async (
    code: string,
    email: string,
    password: string
): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any };
    message?: string;
    error?: { message?: string; code?: string };
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/accept-enterprise-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, email, password }),
        });
        const data = await response.json();
        if (!response.ok) {
            const requestError = new Error(
                data.error?.message || data.message || 'Failed to accept invite'
            ) as Error & { code?: string };
            requestError.code = data.error?.code;
            throw requestError;
        }
        return data;
    } catch (error: any) {
        console.error('Accept enterprise admin invite error:', error);
        throw error;
    }
};

export interface SuperAdminSummary {
    userId: string;
    email: string;
    firstName: string;
    lastName?: string;
    lastLogin?: string;
}

export interface PendingSuperAdminInvite {
    email: string;
    inviteLink: string;
    expiresAt: string;
    createdAt: string;
}

export interface SuperAdminAccessSummary {
    superAdmins: SuperAdminSummary[];
    pendingInvites: PendingSuperAdminInvite[];
}

export const getSuperAdminAccess = async (): Promise<{
    success: boolean;
    data?: SuperAdminAccessSummary;
}> => {
    const response = await apiClient.get<SuperAdminAccessSummary>('/enterprise/super-admin-invites');
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to load super admin settings');
    }
    return response as { success: boolean; data?: SuperAdminAccessSummary };
};

export const createSuperAdminInvite = async (
    email: string
): Promise<{
    success: boolean;
    data?: { inviteLink: string; expiresAt: string; isExistingUser: boolean };
}> => {
    const response = await apiClient.post<{ inviteLink: string; expiresAt: string; isExistingUser: boolean }>(
        '/enterprise/super-admin-invites',
        { email }
    );
    if (!response.success) {
        throw new Error(response.error?.message || response.message || 'Failed to create super admin invite');
    }
    return response as {
        success: boolean;
        data?: { inviteLink: string; expiresAt: string; isExistingUser: boolean };
    };
};

export const validateSuperAdminInviteToken = async (token: string): Promise<{
    success: boolean;
    data?: {
        email: string;
        inviterName: string;
        isExistingUser: boolean;
        existingUserName?: string | null;
    };
    message?: string;
}> => {
    try {
        const response = await fetch(
            `${API_CONFIG.BASE_URL}/auth/validate-super-admin-invite?token=${encodeURIComponent(token)}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Invalid or expired invite');
        }
        return data;
    } catch (error: any) {
        console.error('Validate super admin invite error:', error);
        throw error;
    }
};

export const registerSuperAdminInvite = async (params: {
    token: string;
    firstName: string;
    lastName: string;
    password: string;
}): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register-super-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Failed to register');
        }
        return data;
    } catch (error: any) {
        console.error('Register super admin invite error:', error);
        throw error;
    }
};

export const acceptSuperAdminInvite = async (
    token: string,
    password?: string
): Promise<{
    success: boolean;
    data?: { tokens: { accessToken: string; refreshToken: string }; user: any };
    message?: string;
}> => {
    try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/accept-super-admin-invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password }),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error?.message || data.message || 'Failed to accept invite');
        }
        return data;
    } catch (error: any) {
        console.error('Accept super admin invite error:', error);
        throw error;
    }
};

export interface AdminSupportTicketPayload {
    category: string;
    subcategory: string;
    issueTitle: string;
    description: string;
    triedSolutionIds: string[];
    followUpAnswers: Array<{ questionId: string; label: string; answer: string }>;
    clubId?: string;
    pageUrl?: string;
    userAgent?: string;
    browserInfo?: {
        language?: string;
        screenSize?: string;
        timezone?: string;
    };
    clientContext?: Record<string, unknown>;
}

export const submitAdminSupportTicket = async (
    payload: AdminSupportTicketPayload
): Promise<{
    success: boolean;
    data?: { ticketId: string };
    message?: string;
}> => {
    const response = await apiClient.post<{ ticketId: string }>(
        '/admin/support/ticket',
        payload
    );

    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to submit support ticket');
    }

    return {
        success: true,
        data: response.data,
        message: response.message
    };
};